import { useCallback, useState } from "react";
import { MAX_BASE64_UPLOAD_SIZE } from "domains/assets/constants/upload";
import useGetUploadedAsset from "domains/assets/hooks/useGetUploadedAsset";
import { getBase64Mime } from "domains/assets/utils";
import { ASSET_TYPE } from "domains/assets/utils/getIsType";
import { getMultipartChunkCount } from "domains/commons/misc";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import { useHandleApiError } from "infra/api/error";
import {
  GetAssetsByAssetIdApiResponse,
  usePostAssetMutation,
  usePostUploadsActionMutation,
  usePostUploadsMutation,
} from "infra/api/generated/api";
import { apiSlice } from "infra/store/apiSlice";
import { API_TAGS } from "infra/store/constants";
import store from "infra/store/store";

export function useAssetUpload() {
  const { selectedProject } = useTeamContext();
  const handleApiError = useHandleApiError();
  const [postAssetTrigger] = usePostAssetMutation();
  const getUploadedAsset = useGetUploadedAsset();
  const [uploadFileTrigger] = usePostUploadsMutation();
  const [runActionOnUploadTrigger] = usePostUploadsActionMutation();
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState<number | undefined>(undefined);

  const uploadImage = useCallback(
    async ({
      data,
      name,
      collectionIds,
      hide,
      parentAssetId,
      kind,
    }: {
      data: string;
      name: string;
      collectionIds?: string[];
      hide?: boolean;
      parentAssetId?: string;
      kind: "image" | "video" | "3d";
    }): Promise<GetAssetsByAssetIdApiResponse["asset"] | undefined> => {
      if (!data) {
        return;
      }

      setIsUploading(true);

      try {
        setProgress(undefined);

        const arrBuffer = await (await fetch(data)).arrayBuffer();
        const base64Size = arrBuffer.byteLength;
        const base64Mime = getBase64Mime(data) ?? "image/jpg";

        if (base64Size < MAX_BASE64_UPLOAD_SIZE && kind === "image") {
          const response = await postAssetTrigger({
            projectId: selectedProject.id,
            body: {
              image: data,
              // not sure why or how but sometimes name is undefined
              name: name ?? "reference",
              collectionIds,
              hide,
              parentId: parentAssetId,
            },
          }).unwrap();
          return response.asset;
        }

        const chunksCount = getMultipartChunkCount(base64Size);
        const chunkSize = Math.ceil(base64Size / chunksCount);
        const uploadInfo = await uploadFileTrigger({
          projectId: selectedProject.id,
          body: {
            // not sure why or how but sometimes name is undefined
            fileName: name ?? "reference",
            kind,
            contentType: base64Mime,
            parts: chunksCount,
            assetOptions: {
              collectionIds,
              hide,
              parentId: parentAssetId,
            },
          },
        }).unwrap();

        if (!uploadInfo.upload.parts) {
          throw new Error("Invalid upload response");
        }

        for (let i = 0; i < chunksCount; i++) {
          const chunk = arrBuffer.slice(i * chunkSize, chunkSize * (i + 1));
          const chunkUrl = (uploadInfo.upload.parts ?? [])[i].url;

          await fetch(chunkUrl, { method: "PUT", body: chunk });
          setProgress(Math.round(((i + 1) / chunksCount) * 80));
        }

        await runActionOnUploadTrigger({
          projectId: selectedProject.id,
          uploadId: uploadInfo.upload.id,
          body: {
            action: "complete",
          },
        }).unwrap();

        const response = await getUploadedAsset(uploadInfo.upload.id);
        store.dispatch(apiSlice.util.invalidateTags([API_TAGS.asset]));

        return response.asset;
      } catch (error) {
        handleApiError(error, `Error uploading ${ASSET_TYPE[kind].singular}`);
      } finally {
        setProgress(undefined);
        setIsUploading(false);
      }
    },
    [
      getUploadedAsset,
      runActionOnUploadTrigger,
      selectedProject.id,
      uploadFileTrigger,
      handleApiError,
      postAssetTrigger,
    ]
  );

  return {
    isUploading,
    uploadProgress: progress,
    uploadImage,
  };
}
